import React, { useState } from 'react';
import { BookOpen, Scale, Heart, AlertTriangle, PawPrint, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const KnowledgeForum = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (index) => {
    setExpandedSection(expandedSection === index ? null : index);
  };

  const knowledgeTopics = [
    {
      title: 'Laws About Stray Animals & Mumbai Societies',
      icon: Scale,
      color: 'from-blue-400 to-sky-500',
      points: [
        'Animal Birth Control (ABC) Rules 2001 - Society cannot relocate sterilized dogs',
        'Prevention of Cruelty to Animals Act 1960 - Feeding strays is a legal right',
        'Bombay Municipal Corporation allows feeding in designated areas',
        'Supreme Court ruling: Societies must allow animal feeders',
        'Residents cannot harm, relocate, or poison stray animals',
        'ABC program is mandatory - sterilization and vaccination must be allowed',
        'Legal action can be taken against those obstructing animal welfare'
      ]
    },
    {
      title: 'Why Coexistence is Important',
      icon: Heart,
      color: 'from-green-400 to-emerald-500',
      points: [
        'Strays control rodent and pest population naturally',
        'Sterilized dogs are territorial and prevent new strays from entering',
        'Community animals provide security - alert residents to strangers',
        'Teaching compassion to children builds empathy',
        'Humane treatment reflects positively on society culture',
        'Coexistence reduces conflict and promotes harmony',
        'ABC programs make stray populations healthier and calmer'
      ]
    },
    {
      title: 'What is Rabies & How to Act',
      icon: AlertTriangle,
      color: 'from-red-400 to-orange-500',
      points: [
        'Rabies is a fatal viral disease transmitted through animal bites',
        'Symptoms in animals: Excessive drooling, aggression, fear of water, paralysis',
        'Symptoms in humans: Fever, headache, fear of water, confusion',
        'If bitten: Wash wound immediately with soap and water for 15 minutes',
        'Seek medical attention within 24 hours - get anti-rabies vaccination',
        'Do NOT wait for symptoms - rabies is 100% fatal once symptoms appear',
        'Report aggressive or sick animals to animal welfare volunteers',
        'Prevention: Ensure society animals are vaccinated regularly'
      ]
    },
    {
      title: 'Bad Touch Good Touch for Cats & Dogs',
      icon: PawPrint,
      color: 'from-purple-400 to-violet-500',
      points: [
        'GOOD TOUCH - Gentle petting on back, chest, and sides',
        'GOOD TOUCH - Scratching behind ears and under chin',
        'GOOD TOUCH - Calm voice and slow movements',
        'BAD TOUCH - Pulling tail, ears, or whiskers',
        'BAD TOUCH - Touching paws, belly without trust',
        'BAD TOUCH - Sudden movements or loud noises',
        'BAD TOUCH - Disturbing while eating or sleeping',
        'Always let the animal approach you first',
        'Read body language: Wagging tail (dogs) = happy, Ears back = scared',
        'Never corner or chase an animal - give them space to escape'
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      <div className="bg-gradient-to-br from-indigo-400 to-purple-500 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="bg-white p-3 rounded-xl shadow-lg">
                <BookOpen className="w-8 h-8 text-indigo-600" />
              </div>
              <h1 className="text-4xl md:text-5xl font-extrabold text-white font-heading" data-testid="knowledge-title">
                Knowledge Forum
              </h1>
            </div>
            <p className="text-lg text-white/90 font-medium">
              Essential information about stray animals, laws, and safe interactions
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-4">
          {knowledgeTopics.map((topic, index) => {
            const Icon = topic.icon;
            const isExpanded = expandedSection === index;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                data-testid={`knowledge-topic-${index}`}
                className="bg-white rounded-2xl shadow-lg border border-orange-100 overflow-hidden"
              >
                <button
                  onClick={() => toggleSection(index)}
                  className="w-full p-6 flex items-center justify-between hover:bg-orange-50 transition-all"
                  data-testid={`topic-header-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`bg-gradient-to-br ${topic.color} p-3 rounded-xl`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-xl font-bold text-text-primary font-heading text-left">
                      {topic.title}
                    </h2>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-6 h-6 text-primary flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-primary flex-shrink-0" />
                  )}
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border-t border-orange-100"
                    >
                      <div className="p-6 bg-gradient-to-br from-orange-50 to-amber-50">
                        <ul className="space-y-3">
                          {topic.points.map((point, idx) => (
                            <li
                              key={idx}
                              className="flex items-start gap-3"
                              data-testid={`topic-point-${index}-${idx}`}
                            >
                              <div className="bg-primary p-1 rounded-full flex-shrink-0 mt-1">
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              </div>
                              <span className="text-text-primary leading-relaxed font-medium">
                                {point}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-orange-200 p-8 mt-8">
          <div className="flex items-start gap-4">
            <BookOpen className="w-8 h-8 text-orange-600 flex-shrink-0" />
            <div>
              <h3 className="text-xl font-bold text-text-primary mb-3 font-heading">Important Reminder</h3>
              <p className="text-text-secondary leading-relaxed mb-3">
                This knowledge forum provides basic information for awareness. For legal matters, consult a lawyer. 
                For medical emergencies, contact veterinarians or doctors immediately.
              </p>
              <p className="text-text-secondary leading-relaxed">
                Always report aggressive animals or suspicious behavior to your society's Animal Welfare Volunteer 
                or Managing Committee Representative.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeForum;